import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Typography,
  useTheme,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import RightArrow from '../../../components/icons/RightArrow';
import FinanceTrialSiteDetailsCard from './FinanceTrialSiteDetailsCard';
import {
  useFinanceDashboradDataQuery,
  useGetFinanceSiteDetailsQuery,
  useUploadAccountCsvMutation,
} from '../../../store/slices/apiSlice';
import { useDispatch, useSelector } from 'react-redux';
import { setFinanceSite } from '../../../store/slices/financeSlice';
import FinanceSiteBudgetPie from './FinanceSiteBudgetPie';
import CustomButton from '../../../components/@extended/CustomButton';
import DownloadTemplate from '../../../components/common/DownloadTemplate';
import CloseIcon from '@mui/icons-material/Close';
import EventsSection from '../../createFinanceTrial/EventsSection';
import SiteTransactionsCard from './SiteTransactionsCard';
import GenerateInvoice from './GenerateInvoice';
import FinanceTrialSiteInvoicesCard from './FinanceTrialSiteInvoicesCard';
import FinanceSiteExpenditureChart from './FinanceSiteExpenditureChart';
import { selectCurrentUser } from '../../../store/slices/authSlice';
import { getUserRole } from '../../util';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { VisuallyHiddenInput } from '../../../components/common/DocumentUpload';
import { openMessage } from '../../../store/slices/showMessageSlice';
import FinanceAuditExport from '../../../components/common/FinanceAuditExport';
export const allowedFileTypes =
  '.csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const FinanceTrialSitedetails = () => {
  const theme = useTheme();
  const user = useSelector(selectCurrentUser);
  const userRole = getUserRole(user);
  const navigate = useNavigate();
  const { trialId, siteId, trialSiteId } = useParams();
  const dispatch = useDispatch();
  const trial = useSelector((state) => state.finance.trial);
  const [uploadAccountCsv] = useUploadAccountCsvMutation();
  const { refetch } = useGetFinanceSiteDetailsQuery(
    {
      trialId,
      siteId,
    },
    { skip: !trialId || !siteId, refetchOnMountOrArgChange: true }
  );
  const { data: dashboardData, isLoading, refetch: refetchDashboardData } = useFinanceDashboradDataQuery({
    data: {
      preference: 'FINANCE_STUDY_BUDGET_OVERVIEW',
      clinicalStudyId: trialId,
      studySiteIds: trialSiteId,
    },
  });
  const [openSiteBudget, setOpenSiteBudget] = useState(false);
  const [openGenerateInvoice, setOpenGenerateInvoice] = useState(false);
  const fileInputRef = useRef(null);
  const handleBack = () => {
    navigate(-1);
    dispatch(setFinanceSite(null));
  };
  const isSponsor = userRole === 'sponsor';
  const selectedSite = useSelector((state) => state.finance?.currentSite);

  const handleCloseBudget = async () => {
    try {
      await refetch();
      setOpenSiteBudget(false);
    } catch (error) {}
  };

  const handleFileUpload = async (acceptedFiles) => {
    try {
      const formData = new FormData();
      formData.append('file', acceptedFiles[0]);
      const result = await uploadAccountCsv({ body: formData }).unwrap();

      dispatch(
        openMessage({
          messageSeverity: 'success',
          message: 'File uploaded successfully! Started Processing...',
        })
      );
    } catch (error) {
      console.error('File upload error:', error);
      const errorMessage = error?.data?.message?.includes('Missing required columns')
        ? 'Please upload a valid file with required columns.'
        : error?.data?.message || 'Document upload failed. Please try again.';

      dispatch(
        openMessage({
          messageSeverity: 'error',
          message: errorMessage,
        })
      );
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.blur();
    }
  };

  return (
    <>
      <Grid container rowGap={2}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button type="text" onClick={handleBack} startIcon={<RightArrow leftArrow />}>
              <Typography variant="subtitle1" sx={{ textTransform: 'none' }}>
                Back to Study Details
              </Typography>
            </Button>
            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
              {isSponsor && (
                <Box>
                  <CustomButton
                    variant="outlined"
                    startIcon={<CloudUploadOutlinedIcon />}
                    component="label"
                    sx={{ mr: 2 }}
                  >
                    Upload Accounts
                    <VisuallyHiddenInput
                      ref={fileInputRef}
                      onChange={(e) => {
                        console.log('e.target.files', e.target.files);
                        handleFileUpload(e.target.files);
                      }}
                      type="file"
                      accept={allowedFileTypes}
                    />
                  </CustomButton>
                </Box>
              )}
              {isSponsor && (
                <Box>
                  <CustomButton variant="outlined" onClick={() => setOpenGenerateInvoice(true)} sx={{ mr: 2 }}>
                    Generate Invoice
                  </CustomButton>
                </Box>
              )}
              <Box>
                <CustomButton variant="contained" onClick={() => setOpenSiteBudget(true)} sx={{ mr: 2 }}>
                  View Budget
                </CustomButton>
              </Box>
              <Box>
                <FinanceAuditExport
                  sx={{ mr: isSponsor ? 2 : 0 }}
                  protocolNumber={trial?.protocolNumber}
                  siteEntityId={siteId}
                  studySiteMappingId={trialSiteId}
                />
              </Box>
              {isSponsor && (
                <Box sx={{ ml: 2 }}>
                  <DownloadTemplate />
                </Box>
              )}
            </Box>
          </Box>
        </Grid>
        <Grid container direction={'row'} columnSpacing={3}>
          <Grid item sm={12} md={8}>
            <Box sx={{ pb: 3 }}>
              <FinanceTrialSiteDetailsCard />
            </Box>
            <Box sx={{ pb: 3 }}>
              <SiteTransactionsCard refetchDashboardData={refetchDashboardData}/>
            </Box>
            <Box sx={{ pb: 0 }}>
              <FinanceTrialSiteInvoicesCard />
            </Box>
          </Grid>
          <Grid item md={4} sm={12}>
            <Box sx={{ pb: 3 }}>
              <FinanceSiteBudgetPie dashboardData={dashboardData} />
            </Box>
            <Box sx={{ pb: 3 }}>
              <FinanceSiteExpenditureChart dashboardData={dashboardData} />
            </Box>
          </Grid>
        </Grid>
      </Grid>
      <Dialog
        fullWidth
        maxWidth={'md'}
        scroll={'paper'}
        open={openSiteBudget}
        onClose={() => {
          setOpenSiteBudget(false);
        }}
        PaperProps={{
          sx: {
            maxWidth: 'md',
          },
        }}
      >
        <DialogTitle>Budget Events</DialogTitle>
        <IconButton
          aria-label="close"
          onClick={() => setOpenSiteBudget(false)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <EventsSection
            protocolNumber={selectedSite?.clinicalStudy?.protocolNumber}
            siteEntityId={selectedSite?.siteEntityId}
            events={selectedSite?.events}
            isEditable={isSponsor}
            onClose={handleCloseBudget}
          />
        </DialogContent>
      </Dialog>
      <Dialog
        fullWidth
        maxWidth={'md'}
        scroll={'paper'}
        open={openGenerateInvoice}
        onClose={() => {
          setOpenGenerateInvoice(false);
        }}
        PaperProps={{
          sx: {
            maxWidth: 'md',
          },
        }}
      >
        <DialogTitle>Generate Invoice</DialogTitle>
        <IconButton
          aria-label="close"
          onClick={() => setOpenGenerateInvoice(false)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <GenerateInvoice
            handleClose={() => {
              setOpenGenerateInvoice(false);
            }}
            isEditable={true}
            trialId={trialId}
            trialSiteId={trialSiteId}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FinanceTrialSitedetails;
